/**
 * src/sync/authToken.js — single source of truth for Firebase ID token retrieval.
 *
 * Why two variants?
 *   - `getFirebaseTokenOrThrow()` — REST callers (api.js, adminApi.js) want a
 *     hard fail with `err.isAuth = true` so callers route to the outbox / show
 *     a re-login toast.
 *   - `getFirebaseTokenOrNull()` — SignalR client wants a soft fail because
 *     the connection will be rejected at the hub anyway and the user sees a
 *     'disconnected' state with no console noise.
 *
 * `getIdToken(false)` returns the cached token until ~5 min before expiry;
 * Firebase auto-refreshes in the background, so the cost is a no-op when the
 * token is still valid.
 */

import { projectAuth } from '@/firebase/config';

/**
 * Resolve a Firebase ID token. Throws with `.isAuth = true` on missing user
 * or token failure. Used by REST callers (api.js, adminApi.js) so the outbox
 * can defer (not consume an attempt) when the user is transiently signed out.
 *
 * @returns {Promise<string>} Firebase ID token
 * @throws {Error} with `.isAuth = true`
 */
export async function getFirebaseTokenOrThrow() {
  const user = projectAuth.currentUser;
  if (!user) {
    const e = new Error('no signed-in Firebase user');
    e.isAuth = true;
    throw e;
  }
  try {
    return await user.getIdToken(/* forceRefresh */ false);
  } catch (err) {
    const e = new Error(`failed to obtain Firebase ID token: ${err && err.message ? err.message : err}`);
    e.isAuth = true;
    e.cause = err;
    throw e;
  }
}

/**
 * Resolve a Firebase ID token, returning null on any failure. Used by the
 * SignalR client where a missing token results in a hub rejection — the user
 * sees a 'disconnected' state without console noise.
 *
 * @returns {Promise<string|null>}
 */
export async function getFirebaseTokenOrNull() {
  const user = projectAuth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken(/* forceRefresh */ false);
  } catch (_) {
    return null;
  }
}

/**
 * Force a fresh token (forceRefresh=true). Used by file upload paths where a
 * long upload could outlive the cached token's TTL. Returns null on any error
 * so the caller can surface a polite message without try/catch boilerplate.
 *
 * @returns {Promise<string|null>}
 */
export async function getFreshFirebaseTokenOrNull() {
  const user = projectAuth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken(/* forceRefresh */ true);
  } catch (_) {
    return null;
  }
}
